import { extensions, MeshPipe, RenderGroup } from 'pixi.js';
import { Game } from '@/core/Game';

// Register the mesh render pipe up front (de-duplicated by name). Pixi 8
// registers it as a side effect of importing Mesh, which has raced the
// renderer's construction; a renderer without it crashes the first time a
// Mesh (the caustic water) takes the update-only render path.
extensions.add(MeshPipe);

/**
 * Guard against a rare Pixi 8 failure that kills the whole game loop:
 * RenderGroup.updateRenderable() (the "only renderables changed" fast path)
 * looks up renderPipes[renderable.renderPipeId] and throws when the pipe
 * is missing. It has been seen a few seconds into Stage 3 under load,
 * intermittently. Skipping that renderable and forcing a full instruction
 * rebuild next frame keeps the loop alive and repairs the render group.
 */
{
  const proto = RenderGroup.prototype as any;
  const orig = proto.updateRenderable;
  let warned = false;
  proto.updateRenderable = function (renderable: any) {
    const pipes = this.instructionSet?.renderPipes;
    if (!renderable || !pipes || !pipes[renderable.renderPipeId]) {
      this.structureDidChange = true;
      if (!warned) {
        warned = true;
        console.warn('[render] renderable without a pipe skipped; rebuilding instructions',
          renderable?.renderPipeId, renderable?.label ?? renderable?.constructor?.name);
      }
      return;
    }
    return orig.call(this, renderable);
  };
}
import { bus } from '@/core/EventBus';
import { installGlobalToolbar } from '@/ui/GlobalToolbar';
import { installAchievementsPanel } from '@/ui/AchievementsPanel';
import { LeaderboardSystem } from '@/systems/LeaderboardSystem';

/**
 * Every level runs fullscreen. Browsers reject requestFullscreen() unless
 * it comes from a user gesture, so: try right away when a level starts
 * (the click that started it usually still counts), and if that didn't
 * take — or the player dropped out of fullscreen with a swipe or Esc —
 * arm a one-shot listener so the next tap in the level brings it back.
 * On phones, fullscreen is also what lets us lock landscape.
 */
function wireAutoFullscreen(): void {
  const isFullscreen = (): boolean =>
    !!(document.fullscreenElement || (document as any).webkitFullscreenElement);

  const enter = (): void => {
    if (isFullscreen()) return;
    const root = document.documentElement as any;
    const fn = root.requestFullscreen || root.webkitRequestFullscreen || root.msRequestFullscreen;
    if (!fn) return;   // iPhone Safari: no element fullscreen — install the app instead
    try {
      const p = fn.call(root, { navigationUI: 'hide' }) as Promise<void> | void;
      p?.then?.(() => {
        const o = screen.orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
        o?.lock?.('landscape').catch(() => { /* not allowed here */ });
      }).catch?.(() => { /* no gesture yet — the armed listener will retry */ });
    } catch { /* unsupported — ignore */ }
  };

  const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const;
  let armed = false;
  const onGesture = (): void => {
    disarm();
    enter();
  };
  const arm = (): void => {
    if (armed || isFullscreen()) return;
    armed = true;
    for (const t of GESTURES) window.addEventListener(t, onGesture, { capture: true });
  };
  const disarm = (): void => {
    armed = false;
    for (const t of GESTURES) window.removeEventListener(t, onGesture, { capture: true });
  };

  arm();                       // the start menu: first tap goes fullscreen
  bus.on('stage:started', () => { enter(); arm(); });
}

async function boot(): Promise<void> {
  const mount = document.getElementById('app');
  if (!mount) throw new Error('Missing #app root');
  wireAutoFullscreen();
  new LeaderboardSystem();
  installGlobalToolbar();
  const game = new Game();
  await game.init(mount);
  // Install the heritage panel after init — game.data is now loaded so
  // the panel's lazy getter will resolve to a populated GameData.
  installAchievementsPanel({ getData: () => game.data });
  // reveal
  const loading = document.getElementById('loading');
  if (loading) {
    loading.classList.add('hidden');
    setTimeout(() => loading.remove(), 800);
  }
  // expose for debugging
  (window as any).__evolve = game;
}

boot().catch((err) => {
  console.error('[boot]', err);
  const el = document.getElementById('loading');
  if (el) el.textContent = 'Boot failed — see console';
});
